import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from '../services/apiService';
import { toast } from 'react-toastify';

const api = apiService();

// ————————————————————————————————
// Async actions for Campaigns
// ————————————————————————————————

export const addCampaign = createAsyncThunk(
  'campaigns/addCampaign',
  async (campaignData: any) => {
    console.log('Sending Campaign Request', campaignData);
    const response = await api.post('/campaign/', campaignData);
    return response.data;
  },
);

export const fetchCampaignStats = createAsyncThunk(
  'campaigns/fetchCampaigns',
  async () => {
    const response = await api.get('/campaign/stats');
    return response.data;
  },
);

export const fetchMyCampaigns = createAsyncThunk(
  'campaigns/fetchMyCampaigns',
  async (
    params: {
      page?: number;
      limit?: number;
      title?: string;
      status?: string;
      categoryName?: string;
    } = {},
  ) => {
    const response = await api.get('/campaign', { params });
    return response.data;
  },
);

export const fetchMyCampaignsAnalytics = createAsyncThunk(
  'campaigns/fetchMyCampaignsAnalytics',
  async ({
    timeRange,
  }: {
    timeRange: 'daily' | 'weekly' | 'monthly' | 'yearly';
  }) => {
    const response = await api.get('/campaign/analytics/me', {
      params: { timeRange },
    });
    return response.data;
  },
);

export const fetchAllCampaignsAnalytics = createAsyncThunk(
  'campaigns/fetchAllCampaignsAnalytics',
  async ({
    timeRange,
  }: {
    timeRange: 'daily' | 'monthly' | 'yearly' | 'weekly';
  }) => {
    const response = await api.get('/campaign/analytics', {
      params: { timeRange },
    });
    return response.data;
  },
);

export const fetchAllCampaignsAdmin = createAsyncThunk(
  'campaigns/fetchAllCampaignsAdmin',
  async (
    params: {
      title?: string;
      category?: string;
      status?: string;
      limit?: number;
      page?: number;
    } = {},
  ) => {
    const response = await api.get('/campaign/all', { params });
    return response.data;
  },
);

export const updateCampaign = createAsyncThunk(
  'campaigns/updateCampaign',
  async ({
    campaignId,
    campaignData,
  }: {
    campaignId: string;
    campaignData: FormData;
  }) => {
    const response = await api.patch(`/campaign/${campaignId}`, campaignData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
);

export const updateCampaignStatus = createAsyncThunk(
  'campaigns/updateCampaignStatus',
  async ({ campaignId, status }: { campaignId: string; status: string }) => {
    const response = await api.patch(`/campaign/status/${campaignId}`, {
      status,
    });
    if (response.status !== 200) {
      toast.error('Failed to update campaign status');
    } else {
      toast.success('Campaign status updated successfully');
    }
    return response.data;
  },
);

export const deleteCampaign = createAsyncThunk(
  'campaigns/deleteCampaign',
  async (campaignId: string) => {
    await api.delete(`/campaign/${campaignId}`);
    return campaignId;
  },
);

export const fetchCampaignById = createAsyncThunk(
  'campaigns/fetchMyCampaignById',
  async (id: string) => {
    const response = await api.get(`/campaign/${id}`);
    return response.data;
  },
);

export const fetchMyAudience = createAsyncThunk(
  'campaigns/fetchMyAudience',
  async (
    params: {
      page?: number;
      limit?: number;
      title?: string;
      status?: string;
      categoryName?: string;
    } = {},
  ) => {
    const response = await api.get('/campaign/audience', { params });
    return response.data;
  },
);

// ————————————————————————————————
// State and Slice
// ————————————————————————————————

interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
}

interface CampaignStats {
  total: number;
  approved: number;
  rejected: number;
  pending: number;
  active: number;
}

interface CampaignState {
  list: any[];
  myList: any[];
  adminList: any[];
  analytics: any | null;
  myAnalytics: any | null;
  adminPagination?: PaginationInfo;
  adminStats: CampaignStats | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialState: CampaignState = {
  list: [],
  myList: [],
  adminList: [],
  analytics: null,
  myAnalytics: null,
  adminPagination: undefined,
  adminStats: null,
  status: 'idle',
  error: null,
};

const campaignSlice = createSlice({
  name: 'campaigns',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // addCampaign
      .addCase(addCampaign.fulfilled, (state, action) => {
        if (!Array.isArray(state.list)) {
          state.list = [];
        }
        state.list.push(action.payload);
      })

      // fetchCampaigns
      .addCase(fetchCampaignStats.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchCampaignStats.fulfilled, (state, action) => {
        state.list = Array.isArray(action.payload?.data)
          ? action.payload.data
          : [];
        state.status = 'succeeded';
      })
      .addCase(fetchCampaignStats.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message ?? null;
      })

      // fetchMyCampaigns
      .addCase(fetchMyCampaigns.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchMyCampaigns.fulfilled, (state, action) => {
        state.myList = action.payload;
        state.status = 'succeeded';
      })
      .addCase(fetchMyCampaigns.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message ?? null;
      })

      // fetchMyCampaignsAnalytics
      .addCase(fetchMyCampaignsAnalytics.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchMyCampaignsAnalytics.fulfilled, (state, action) => {
        state.myAnalytics = action.payload;
        state.status = 'succeeded';
      })
      .addCase(fetchMyCampaignsAnalytics.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message ?? null;
      })

      // fetchAllCampaignsAnalytics
      .addCase(fetchAllCampaignsAnalytics.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchAllCampaignsAnalytics.fulfilled, (state, action) => {
        state.analytics = action.payload;
        state.status = 'succeeded';
      })
      .addCase(fetchAllCampaignsAnalytics.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message ?? null;
      })

      // fetchAllCampaignsAdmin
      .addCase(fetchAllCampaignsAdmin.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchAllCampaignsAdmin.fulfilled, (state, action) => {
        const { data, pagination, stats } = action.payload;
        const requestedPage = action.meta.arg?.page ?? 1;
        state.adminList =
          requestedPage > 1 ? [...state.adminList, ...data] : data;
        state.adminPagination = pagination ?? null;
        state.adminStats = stats ?? null;
        state.status = 'succeeded';
      })
      .addCase(fetchAllCampaignsAdmin.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message ?? null;
      })

      // updateCampaign
      .addCase(updateCampaign.fulfilled, (state, action) => {
        const idx = state.list.findIndex((c) => c.id === action.payload.id);
        if (idx !== -1) state.list[idx] = action.payload;
      })

      // updateCampaignStatus
      .addCase(updateCampaignStatus.fulfilled, (state, action) => {
        const idx = state.adminList.findIndex(
          (c) => c.id === action.payload.data[0].id,
        );
        if (idx !== -1) {
          state.adminList[idx].status = action.payload.data[0].status;
        }
      })

      // deleteCampaign
      .addCase(deleteCampaign.fulfilled, (state, action) => {
        state.adminList = state.adminList.filter((c) => c.id !== action.payload);
      })

      // fetchMyCampaigns
      .addCase(fetchMyAudience.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchMyAudience.fulfilled, (state, action) => {
        state.myList = action.payload;
        state.status = 'succeeded';
      })
      .addCase(fetchMyAudience.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message ?? null;
      })

      // fetchMyCampaigns
      .addCase(fetchCampaignById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchCampaignById.fulfilled, (state, action) => {
        state.myList = action.payload;
        state.status = 'succeeded';
      })
      .addCase(fetchCampaignById.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message ?? null;
      });
  },
});

export default campaignSlice.reducer;
